// Style lens: restyles the Services section (#skills) in a chosen design language.
// Each lens is one stylesheet scoped to #skills[data-lens="<key>"], loaded on first use together
// with its Google Fonts. Switching wipes the new look in as a circle from the chosen chip
// (View Transitions; instant without support or with reduced motion). ?lens=<key> deep-links.
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

type Lens = {
  css?: () => Promise<unknown>;
  /** Google Fonts css2 query (may hold several families joined by '&family=') */
  fonts?: string;
  /** Font shorthands to wait for, e.g. ['800 1em Manrope'] */
  faces?: string[];
};

const LENSES: Record<string, Lens> = {
  nrg: {},
  skeuo: { css: () => import('../styles/lens/skeuo.css'), fonts: 'Saira+Condensed:wght@500;700&family=IBM+Plex+Sans:wght@400;500', faces: ['700 1em "Saira Condensed"', '400 1em "IBM Plex Sans"'] },
  neu: { css: () => import('../styles/lens/neu.css'), fonts: 'Manrope:wght@400;600;800', faces: ['800 1em Manrope'] },
  glass: { css: () => import('../styles/lens/glass.css'), fonts: 'Sora:wght@300;400;600', faces: ['600 1em Sora'] },
  illus: { css: () => import('../styles/lens/illus.css'), fonts: 'Caveat+Brush&family=Nunito:wght@600;800', faces: ['1em "Caveat Brush"', '600 1em Nunito'] },
  motion: { css: () => import('../styles/lens/motion.css') },
  clay: { css: () => import('../styles/lens/clay.css'), fonts: 'Nunito:wght@600;800;900', faces: ['900 1em Nunito'] },
  pixel: { css: () => import('../styles/lens/pixel.css'), fonts: 'Press+Start+2P&family=VT323', faces: ['1em "Press Start 2P"', '1em VT323'] },
  cyber: { css: () => import('../styles/lens/cyber.css'), fonts: 'Orbitron:wght@700;900&family=Rajdhani:wght@500;700', faces: ['900 1em Orbitron', '500 1em Rajdhani'] },
  brutal: { css: () => import('../styles/lens/brutal.css') },
  memphis: { css: () => import('../styles/lens/memphis.css'), fonts: 'Bungee&family=Rubik:wght@500;700;800', faces: ['1em Bungee', '500 1em Rubik'] },
  maximal: { css: () => import('../styles/lens/maximal.css'), fonts: 'Abril+Fatface&family=Yatra+One&family=Oswald:wght@600;700&family=Mukta:wght@500;600', faces: ['1em "Abril Fatface"', '1em "Yatra One"', '700 1em Oswald'] },
  minimal: { css: () => import('../styles/lens/minimal.css'), fonts: 'Instrument+Sans:wght@400;500;600', faces: ['500 1em "Instrument Sans"'] },
};

/** Lenses with a light background: the fixed nav switches to dark text over them. */
const LIGHT = new Set(['minimal', 'brutal', 'neu', 'clay', 'memphis', 'maximal', 'illus']);

const WIPE_MS = 750;
const FONT_WAIT_MS = 900; // never hold a switch hostage to a slow font; it swaps in when ready

const prepared = new Map<string, Promise<void>>();

function prepare(key: string): Promise<void> {
  let job = prepared.get(key);
  if (job) return job;
  const lens = LENSES[key];
  const fonts = lens.fonts
    ? new Promise<void>((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${lens.fonts}&display=swap`;
        link.onload = () => { Promise.allSettled((lens.faces ?? []).map((f) => document.fonts.load(f))).then(() => resolve()); };
        link.onerror = () => resolve();
        document.head.append(link);
      })
    : Promise.resolve();
  job = Promise.all([
    lens.css ? lens.css() : null,
    Promise.race([fonts, new Promise((r) => setTimeout(r, FONT_WAIT_MS))]),
  ]).then(() => {});
  prepared.set(key, job);
  return job;
}

/** Motion UI lens: cards assemble as the section scrolls in, and a progress line fills. Returns cleanup. */
function startMotionLens(section: HTMLElement): () => void {
  const cards = [...section.querySelectorAll<HTMLElement>('.svc')];
  const ctx = gsap.context(() => {
    cards.forEach((card, i) => {
      gsap.fromTo(card,
        { '--ay': '90px', '--ar': `${i % 2 ? 3 : -3}deg`, opacity: 0 },
        { '--ay': '0px', '--ar': '0deg', opacity: 1, ease: 'power2.out',
          scrollTrigger: { trigger: card, start: 'top 98%', end: 'top 62%', scrub: true } });
    });
    ScrollTrigger.create({
      trigger: section,
      start: 'top bottom',
      end: 'bottom bottom',
      onUpdate: (s) => section.style.setProperty('--mo-progress', s.progress.toFixed(4)),
    });
  });
  ScrollTrigger.refresh();
  return () => {
    ctx.revert();
    section.style.removeProperty('--mo-progress');
    cards.forEach((c) => ['--rx', '--ry', '--bx', '--by'].forEach((p) => c.style.removeProperty(p)));
    section.querySelectorAll<HTMLElement>('.svc-btn').forEach((b) => ['--bx', '--by'].forEach((p) => b.style.removeProperty(p)));
  };
}

export function initStyleLens(): void {
  const section = document.getElementById('skills');
  const rail = section?.querySelector<HTMLElement>('.lens-rail');
  const chips = [...document.querySelectorAll<HTMLButtonElement>('.lens-chip')];
  if (!section || !rail || !chips.length) return;

  let current = section.dataset.lens ?? 'nrg';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let stopMotion: (() => void) | null = null;

  // The travelling pill sits behind the chosen tab; the one-row rail slides to keep that tab centred
  const syncPill = (smooth = true) => {
    const chip = chips.find((c) => c.dataset.lens === current);
    if (!chip) return;
    rail.style.setProperty('--pill-x', `${chip.offsetLeft}px`);
    rail.style.setProperty('--pill-w', `${chip.offsetWidth}px`);
    rail.style.setProperty('--pill-y', `${chip.offsetTop}px`);
    rail.style.setProperty('--pill-h', `${chip.offsetHeight}px`);
    rail.scrollTo({ left: chip.offsetLeft - (rail.clientWidth - chip.offsetWidth) / 2, behavior: smooth && !reduce.matches ? 'smooth' : 'auto' });
  };

  let underNav = false;
  const syncNav = () => document.documentElement.classList.toggle('nav-on-light', underNav && LIGHT.has(current));

  const apply = (key: string) => {
    section.dataset.lens = key;
    current = key;
    syncNav();
    chips.forEach((c) => {
      const on = c.dataset.lens === key;
      c.setAttribute('aria-checked', String(on));
      c.tabIndex = on ? 0 : -1;
    });
    syncPill();
    stopMotion?.();
    stopMotion = key === 'motion' && !reduce.matches ? startMotionLens(section) : null;
    const url = new URL(window.location.href);
    if (key === 'nrg') url.searchParams.delete('lens');
    else url.searchParams.set('lens', key);
    history.replaceState(history.state, '', url);
  };

  async function select(key: string, from?: HTMLElement) {
    if (key === current || !LENSES[key]) return;
    await prepare(key);
    const doc = document as Document & { startViewTransition?: (cb: () => void) => { ready: Promise<void> } };
    if (!from || reduce.matches || !doc.startViewTransition) { apply(key); return; }

    const s = section!.getBoundingClientRect();
    const c = from.getBoundingClientRect();
    const x = c.left + c.width / 2 - s.left;
    const y = c.top + c.height / 2 - s.top;
    const r = Math.hypot(Math.max(x, s.width - x), Math.max(y, s.height - y));
    const t = doc.startViewTransition(() => apply(key));
    t.ready.then(() => {
      document.documentElement.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${r}px at ${x}px ${y}px)`] },
        { duration: WIPE_MS, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', pseudoElement: '::view-transition-new(lens-section)' },
      );
    }).catch(() => {});
  }

  chips.forEach((chip, i) => {
    chip.addEventListener('click', () => select(chip.dataset.lens!, chip));
    chip.addEventListener('pointerenter', () => { prepare(chip.dataset.lens!); }, { once: true });
    // Radio-group keyboard: arrows move and select
    chip.addEventListener('keydown', (e) => {
      const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const next = chips[(i + step + chips.length) % chips.length];
      next.focus();
      select(next.dataset.lens!, next);
    });
  });

  // ‹ › travel to the previous / next lens
  section.querySelectorAll<HTMLButtonElement>('.lens-step').forEach((b) => b.addEventListener('click', () => {
    const i = chips.findIndex((c) => c.dataset.lens === current);
    const next = chips[(i + Number(b.dataset.step) + chips.length) % chips.length];
    select(next.dataset.lens!, next);
  }));
  syncPill(false);
  document.fonts?.ready.then(() => syncPill(false));
  // Re-measure whenever a tab changes size (lens CSS and web fonts land after the switch), so the pill never drifts
  const ro = new ResizeObserver(() => syncPill(false));
  chips.forEach((c) => ro.observe(c));
  ro.observe(rail);

  // Decorative lens animation runs only while the section is on screen
  new IntersectionObserver(([entry]) => section.classList.toggle('lens-live', entry.isIntersecting)).observe(section);
  // ...and the nav reads it as light only while the section is behind the nav's band at the top
  new IntersectionObserver(([entry]) => { underNav = entry.isIntersecting; syncNav(); }, { rootMargin: '-16px 0px -88% 0px' }).observe(section);

  // Pointer effects (CSS vars only; the lens stylesheets turn them into transforms)
  section.addEventListener('pointermove', (e) => {
    if (current !== 'glass' && current !== 'motion') return;
    const card = (e.target as HTMLElement).closest<HTMLElement>('.svc');
    if (!card) return;
    const r = card.getBoundingClientRect();
    if (current === 'glass') {
      // Glass: a specular highlight follows the pointer across the pane
      card.style.setProperty('--mx', `${e.clientX - r.left}px`);
      card.style.setProperty('--my', `${e.clientY - r.top}px`);
      return;
    }
    if (reduce.matches) return;
    // Motion: the card tilts toward the pointer; its button pulls toward it within reach
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    card.style.setProperty('--rx', `${(-py * 7).toFixed(2)}deg`);
    card.style.setProperty('--ry', `${(px * 9).toFixed(2)}deg`);
    const btn = card.querySelector<HTMLElement>('.svc-btn')!;
    const b = btn.getBoundingClientRect();
    const dx = e.clientX - (b.left + b.width / 2);
    const dy = e.clientY - (b.top + b.height / 2);
    const near = Math.hypot(dx, dy) < 110;
    btn.style.setProperty('--bx', `${near ? (dx * 0.25).toFixed(1) : 0}px`);
    btn.style.setProperty('--by', `${near ? (dy * 0.3).toFixed(1) : 0}px`);
  });
  section.addEventListener('pointerout', (e) => {
    if (current !== 'motion') return;
    const card = (e.target as HTMLElement).closest<HTMLElement>('.svc');
    if (!card || card.contains(e.relatedTarget as Node)) return;
    ['--rx', '--ry'].forEach((p) => card.style.removeProperty(p));
    const btn = card.querySelector<HTMLElement>('.svc-btn')!;
    ['--bx', '--by'].forEach((p) => btn.style.removeProperty(p));
  });

  // Motion: a pill glides to the hovered chip
  rail.addEventListener('pointerover', (e) => {
    if (current !== 'motion') return;
    const chip = (e.target as HTMLElement).closest<HTMLElement>('.lens-chip');
    if (!chip) return;
    rail.style.setProperty('--mo-x', `${chip.offsetLeft}px`);
    rail.style.setProperty('--mo-y', `${chip.offsetTop}px`);
    rail.style.setProperty('--mo-w', `${chip.offsetWidth}px`);
    rail.style.setProperty('--mo-h', `${chip.offsetHeight}px`);
    rail.style.setProperty('--mo-o', '1');
  });
  rail.addEventListener('pointerleave', () => rail.style.setProperty('--mo-o', '0'));

  // "Discuss this" pre-fills the contact brief (only if the visitor hasn't written one yet)
  section.querySelectorAll<HTMLAnchorElement>('.svc-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const brief = document.getElementById('cfBrief') as HTMLTextAreaElement | null;
      if (!brief) return;
      if (!brief.value.trim()) brief.value = `I'm interested in ${btn.dataset.service}. `;
      brief.focus({ preventScroll: true });
    });
  });

  const fromUrl = new URLSearchParams(window.location.search).get('lens');
  if (fromUrl && LENSES[fromUrl] && fromUrl !== current) prepare(fromUrl).then(() => apply(fromUrl));
}
